import { apiFetch } from '../utils/apiClient';
import type { Task } from '../types';

/**
 * ADR 0010 typed client for `/templates/*` + the single-item checklist
 * toggle. Any member of a group can CRUD templates in that group; auth is
 * enforced server-side.
 */

export interface TemplateChecklistItem {
  id: string;
  label: string;
}

export interface Template {
  templateId: string;
  groupId: string;
  title: string;
  description?: string;
  checklistItems: TemplateChecklistItem[];
  scheduleDays: number[];
  defaultAssigneeSub?: string;
  active: boolean;
  createdBy: string;
  createdAt: string;
  lastGeneratedDate?: string;
  /** Enriched by the list endpoint; undefined on single-record responses. */
  groupDisplayName?: string;
}

export interface CreateTemplateInput {
  groupId: string;
  title: string;
  description?: string;
  checklistItems: { label: string }[];
  scheduleDays: number[];
  defaultAssigneeSub?: string;
  active: boolean;
}

export interface UpdateTemplateInput {
  title?: string;
  description?: string;
  checklistItems?: { id?: string; label: string }[];
  scheduleDays?: number[];
  defaultAssigneeSub?: string | null;
  active?: boolean;
}

export async function listTemplates(): Promise<Template[]> {
  const { templates } = await apiFetch<{ templates: Template[] }>('/templates');
  return templates;
}

export async function createTemplate(input: CreateTemplateInput): Promise<Template> {
  const { template } = await apiFetch<{ template: Template }>('/templates', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return template;
}

export async function updateTemplate(
  groupId: string,
  templateId: string,
  patch: UpdateTemplateInput
): Promise<Template> {
  const qs = `?group=${encodeURIComponent(groupId)}`;
  const { template } = await apiFetch<{ template: Template }>(
    `/templates/${encodeURIComponent(templateId)}${qs}`,
    {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }
  );
  return template;
}

export async function deleteTemplate(
  groupId: string,
  templateId: string
): Promise<void> {
  const qs = `?group=${encodeURIComponent(groupId)}`;
  await apiFetch<void>(`/templates/${encodeURIComponent(templateId)}${qs}`, {
    method: 'DELETE',
  });
}

export async function launchTemplate(
  groupId: string,
  templateId: string
): Promise<Task> {
  const qs = `?group=${encodeURIComponent(groupId)}`;
  const { task } = await apiFetch<{ task: Task }>(
    `/templates/${encodeURIComponent(templateId)}/launch${qs}`,
    { method: 'POST' }
  );
  return task;
}

/**
 * PATCH a single checklist item's `done` state. Prefer this over sending the
 * whole `task.checklist` back via `updateTask` — it's one round-trip and the
 * backend handles the auto-complete-on-final-done transition atomically.
 */
export async function toggleChecklistItem(
  teamId: string,
  taskId: string,
  itemId: string,
  done: boolean
): Promise<Task> {
  const { task } = await apiFetch<{ task: Task }>(
    `/tasks/${encodeURIComponent(teamId)}/${encodeURIComponent(taskId)}/checklist/${encodeURIComponent(itemId)}`,
    {
      method: 'PATCH',
      body: JSON.stringify({ done }),
    }
  );
  return task;
}
